import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * StationLiveChat — listener-side live chat box that hangs off the
 * station/host-session pair. Posts to /api/live/{stream_id}/chat (existing
 * backend endpoint) when an active stream is going, otherwise hides itself.
 *
 * Polls every 4s for new messages — cheap, simple, and avoids the
 * complexity of a websocket round-trip when ~50 listeners is the realistic
 * concurrent ceiling for an indie radio station.
 *
 * Shown on /radio/station/{slug} only when host_session.is_live AND
 * a `live_stream_id` is attached to the session (that's the FK chat
 * messages bind to).
 */
export default function StationLiveChat({ slug, hostName }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Pause polling when tab is hidden — saves mobile battery and
      // server load. Resumes on visibilitychange below.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get(`/radio/host/${slug}/chat`, { params: { limit: 50 } });
        if (cancelled) return;
        // Backend returns newest-first — flip so timeline reads top-down.
        const ordered = Array.isArray(data) ? data.slice().reverse() : [];
        setMsgs(ordered);
        // Snap-scroll to bottom on update so new messages land in view.
        setTimeout(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        }, 30);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    // Re-fetch immediately when the tab becomes visible again so the
    // listener doesn't see a stale chat after coming back from another
    // app.
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [slug]);

  const send = async (e) => {
    e?.preventDefault();
    if (!user) {
      toast.info("Sign in to chat");
      return;
    }
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/radio/host/${slug}/chat`, { message: t });
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  if (!slug) return null;
  return (
    <section data-testid="radio-station-live-chat" className="rounded-xl border border-white/10 bg-black/40 mt-6">
      <header className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="w-4 h-4 text-emerald-300" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Live chat</h3>
          {hostName && <span className="text-[10px] text-white/45 truncate">with {hostName}</span>}
        </div>
        <span className="text-[10px] text-white/40">{msgs.length} msg</span>
      </header>
      <div ref={listRef} className="max-h-[280px] overflow-y-auto px-4 py-3 space-y-2">
        {msgs.length === 0 ? (
          <p className="text-[11px] text-white/40 italic">Be the first to say hi.</p>
        ) : (
          msgs.map((m) => (
            <div key={m.id} data-testid={`radio-station-chat-msg-${m.id}`} className="flex items-start gap-2 text-[12px]">
              {m.user_avatar ? (
                <img src={m.user_avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-tv-accent/30 text-tv-accent grid place-items-center text-[10px] font-black shrink-0">
                  {(m.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-white/55 text-[10px] font-bold">{m.user_name || "Anon"}</div>
                <div className="text-white whitespace-pre-wrap break-words">{m.message}</div>
              </div>
            </div>
          ))
        )}
        {error && <p className="text-rose-300 text-[10px]">{error}</p>}
      </div>
      <form onSubmit={send} className="border-t border-white/10 px-3 py-2 flex items-center gap-2">
        <input
          data-testid="radio-station-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={user ? "Say something…" : "Sign in to chat"}
          disabled={!user || sending}
          maxLength={300}
          className="flex-1 bg-black/40 border border-white/10 focus:border-emerald-400/60 outline-none rounded px-3 py-2 text-[12px] text-white placeholder-white/30"
        />
        <button
          data-testid="radio-station-chat-send"
          type="submit"
          disabled={!user || sending || !text.trim()}
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black px-3 py-2 rounded inline-flex items-center"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </section>
  );
}
